/**
 * ProbefahrtSignPage — öffentliches, passwortloses Signatur-Portal für die
 * Probefahrt-Vereinbarung (Jul-2026, User-Wunsch). Der Kunde öffnet den vom
 * Händler gesendeten Link/QR am Handy oder Tablet, verifiziert sich mit dem
 * Nachnamen und unterschreibt auf dem Touchscreen.
 */
import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { PenLine, Eraser, Loader2, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function SignatureCanvas({ label, testid, onChange }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const last = useRef({ x: 0, y: 0 });
    const pos = (e) => {
        const c = canvasRef.current; const rect = c.getBoundingClientRect();
        return { x: (e.clientX - rect.left) * (c.width / rect.width), y: (e.clientY - rect.top) * (c.height / rect.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ } };
    const move = (e) => {
        if (!drawing.current) return; e.preventDefault();
        const ctx = canvasRef.current.getContext("2d"); const p = pos(e);
        ctx.strokeStyle = "#111827"; ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
        last.current = p; dirty.current = true;
    };
    const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(canvasRef.current.toDataURL("image/png")); };
    const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(""); };
    useEffect(() => { const c = canvasRef.current; if (c) { c.width = 600; c.height = 180; } }, []);
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{label}</span>
                <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-red-600" data-testid={`${testid}-clear`}>
                    <Eraser className="w-3.5 h-3.5" /> Löschen
                </button>
            </div>
            <canvas ref={canvasRef}
                onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
                style={{ touchAction: "none" }}
                className="w-full h-[150px] bg-white border-2 border-dashed border-gray-300 rounded-sm cursor-crosshair"
                data-testid={testid} />
            <p className="text-[10px] text-gray-400">Mit Maus (Desktop) oder Finger/Stift (Touch) unterschreiben.</p>
        </div>
    );
}

export default function ProbefahrtSignPage() {
    const { token } = useParams();
    const [step, setStep] = useState("verify"); // verify | sign | done
    const [ln, setLn] = useState("");
    const [info, setInfo] = useState(null);
    const [branding, setBranding] = useState(null);
    const [sig, setSig] = useState("");
    const [place, setPlace] = useState("");
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetch(`${BASE}/api/public/testdrive-sign/${token}/branding`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setBranding(d))
            .catch(() => {});
    }, [token]);

    const post = async (path, body) => {
        const r = await fetch(`${BASE}/api/public/testdrive-sign/${token}/${path}`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Fehler");
        return data;
    };

    const verify = async (e) => {
        e.preventDefault();
        if (!ln.trim()) { toast.error("Bitte Nachname eingeben"); return; }
        setBusy(true);
        try {
            const data = await post("verify", { last_name: ln });
            setInfo(data);
            setStep("sign");
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const submit = async () => {
        if (!sig) { toast.error("Bitte zuerst unterschreiben"); return; }
        setBusy(true);
        try {
            await post("submit", { last_name: ln, customer_signature: sig, place });
            setStep("done");
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const pdfUrl = `${BASE}/api/public/testdrive-sign/${token}/document.pdf?ln=${encodeURIComponent(ln)}`;

    return (
        <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
            <Toaster position="top-center" richColors />
            <header className="bg-white px-4 py-3 flex items-center justify-center border-b border-gray-200 shadow-sm">
                <DealerBrandLogo variant="light" logoUrl={branding?.dealer_logo} companyName={branding?.company_name}
                    imgClassName="h-9 max-w-[180px]" textClassName="text-xl" />
            </header>

            <main className="flex-1 w-full max-w-lg mx-auto px-4 py-6">
                {step === "verify" && (
                    <form onSubmit={verify} className="bg-white border border-gray-200 rounded-sm p-5 space-y-4" data-testid="td-sign-verify-form">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-[#FF8300]" />
                            <h1 className="font-display font-black text-xl">Probefahrt-Vereinbarung unterschreiben</h1>
                        </div>
                        <p className="text-sm text-gray-600">Bitte bestätigen Sie Ihre Identität, um die Vereinbarung anzusehen und zu unterschreiben.</p>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachname</label>
                            <input value={ln} onChange={(e) => setLn(e.target.value)} className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="td-sign-verify-lastname" />
                        </div>
                        <button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider rounded-sm py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="td-sign-verify-submit">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Weiter
                        </button>
                    </form>
                )}

                {step === "sign" && info && (
                    <div className="space-y-4" data-testid="td-sign-sign-step">
                        <div className="bg-white border border-gray-200 rounded-sm p-4">
                            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{info.doc_label}</div>
                            <div className="font-display font-black text-lg">{info.vehicle_label}</div>
                            {info.already_signed && <div className="mt-1 text-xs text-emerald-700 font-semibold">Bereits unterschrieben — eine erneute Unterschrift überschreibt die vorherige.</div>}
                            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#FF8300] hover:underline" data-testid="td-sign-pdf-link">
                                <FileText className="w-4 h-4" /> Vereinbarung als PDF öffnen
                            </a>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-4">
                            <SignatureCanvas label="Ihre Unterschrift" testid="td-sign-canvas" onChange={setSig} />
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Ort</label>
                                <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="z. B. Berlin" className="w-full mt-1 rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="td-sign-place" />
                            </div>
                            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer" data-testid="td-sign-consent">
                                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-orange-500" />
                                Ich habe die Probefahrt-Vereinbarung (PDF oben) gelesen und erkenne die darin genannten Bedingungen (Versicherung/Selbstbeteiligung, Kosten, Datenverarbeitung) an.
                            </label>
                            <button onClick={submit} disabled={busy || !sig || !agree} className="w-full bg-[#FF8300] hover:bg-[#e57600] disabled:opacity-50 text-white font-bold uppercase tracking-wider rounded-sm py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="td-sign-submit-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Rechtsverbindlich unterschreiben
                            </button>
                        </div>
                    </div>
                )}

                {step === "done" && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 text-center space-y-3" data-testid="td-sign-done">
                        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                        <h1 className="font-display font-black text-xl">Vielen Dank!</h1>
                        <p className="text-sm text-gray-600">Ihre Unterschrift wurde erfolgreich übermittelt. Gute Fahrt!</p>
                    </div>
                )}
            </main>
        </div>
    );
}
